import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, errorMessage } from "../lib/api";

const empty = { name: "", email: "", company: "", country: "", address: "", notes: "" };
const inputCls = "w-full bg-transparent border-b border-[#1f4a33]/25 py-2.5 text-[#10271a] placeholder:text-[#5e6b61]/60 focus:outline-none focus:border-[#1f4a33] transition-colors";

export default function SampleRequestDialog({ open, onOpenChange, commodity, lot }) {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [reference, setReference] = useState(null);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const close = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(() => { setForm(empty); setReference(null); }, 300);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.country) return toast.error("Name, email and country are required.");
    setBusy(true);
    try {
      const { data } = await api.post("/enquiries/sample", { commodity, lot_id: lot.id, lot_name: lot.name, ...form });
      setReference(data.reference);
      toast.success("Sample request sent — we'll confirm dispatch by email.");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-[#f6f1e7] border-[#10271a]/10 rounded-3xl sm:max-w-lg p-8" data-testid="sample-dialog">
        {reference ? (
          <div className="text-center py-4" data-testid="sample-confirmation">
            <span className="mx-auto h-14 w-14 rounded-full bg-[#1f4a33] text-[#f6f1e7] flex items-center justify-center"><Check size={24} /></span>
            <DialogHeader className="mt-6 items-center">
              <DialogTitle className="font-display text-3xl font-light text-[#10271a]">Sample on its way.</DialogTitle>
              <DialogDescription className="text-[#5e6b61] mt-2">
                Reference <span className="font-semibold text-[#10271a]" data-testid="sample-reference">{reference}</span>. We'll email you when the {lot?.name} sample is dispatched.
              </DialogDescription>
            </DialogHeader>
            <button type="button" onClick={() => close(false)} data-testid="sample-done" className="mt-8 bg-[#1f4a33] text-[#f6f1e7] px-7 py-3.5 rounded-full text-sm tracking-[0.12em] uppercase font-semibold hover:bg-[#10271a] transition-colors">Done</button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <DialogHeader>
              <p className="text-[#7c5230] text-[11px] tracking-[0.25em] uppercase">Sample request · {lot?.name}</p>
              <DialogTitle className="font-display text-3xl font-light text-[#10271a] leading-tight">Taste it <em className="italic text-[#1f4a33]">before you buy.</em></DialogTitle>
              <DialogDescription className="text-[#5e6b61]">{commodity === "wine" ? "We send a tasting case of sample bottles (or a 750 ml bulk sample) to trade buyers, free of charge." : "We send 250 g green / 500 g cocoa / 50 g vanilla samples to trade buyers, free of charge."}</DialogDescription>
            </DialogHeader>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
              <Field label="Name *"><input className={inputCls} value={form.name} onChange={update("name")} placeholder="Your name" data-testid="sample-name" /></Field>
              <Field label="Email *"><input className={inputCls} type="email" value={form.email} onChange={update("email")} placeholder="you@company.com" data-testid="sample-email" /></Field>
              <Field label="Company"><input className={inputCls} value={form.company} onChange={update("company")} placeholder="Roastery / maker" data-testid="sample-company" /></Field>
              <Field label="Country *"><input className={inputCls} value={form.country} onChange={update("country")} placeholder="Destination country" data-testid="sample-country" /></Field>
            </div>
            <div className="mt-5"><Field label="Shipping address"><textarea className={`${inputCls} resize-none`} rows={2} value={form.address} onChange={update("address")} placeholder="Street, city, postcode" data-testid="sample-address" /></Field></div>
            <div className="mt-5"><Field label="Notes"><input className={inputCls} value={form.notes} onChange={update("notes")} placeholder="Roast level, intended use..." data-testid="sample-notes" /></Field></div>
            <button type="submit" disabled={busy} data-testid="sample-submit" className="mt-8 w-full group inline-flex items-center justify-center gap-3 bg-[#1f4a33] text-[#f6f1e7] px-7 py-4 rounded-full text-sm tracking-[0.12em] uppercase font-semibold hover:bg-[#10271a] transition-colors disabled:opacity-60">
              {busy ? "Sending..." : "Request sample"}
              {!busy && <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] tracking-[0.18em] uppercase text-[#5e6b61]">{label}</span>
      {children}
    </label>
  );
}
